import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, Heart } from "lucide-react";
import { WhatsAppIcon, WHATSAPP_URL } from "./WhatsAppIcon";

const HERO_IMG = "/hero-foto.webp";

const HERO_LINES = [
    <>Ogni giorno,</>,
    <>un amico</>,
    <>
        <em className="italic text-terracotta">accanto a te.</em>
    </>,
];

const CHIPS = ["Spese", "Visite mediche", "Accompagnamento", "Passeggiate"];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yImg = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const yBadge = useTransform(scrollYProgress, [0, 1], [0, 40]);

    return (
        <section
            ref={ref}
            className="relative pt-[150px] lg:pt-[190px] pb-20 lg:pb-28"
        >
            <div
                aria-hidden="true"
                className="absolute -top-24 right-[-10%] h-[480px] w-[480px] rounded-full bg-sage/15 blur-3xl"
            />
            <div
                aria-hidden="true"
                className="absolute bottom-0 left-[-8%] h-[380px] w-[380px] rounded-full bg-terracotta/10 blur-3xl"
            />

            <div className="relative mx-auto max-w-7xl px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                        className="mb-6 inline-flex items-center gap-2 rounded-full border border-line bg-stonewarm px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-terracotta"
                        data-testid="hero-eyebrow"
                    >
                        Accompagnamento e assistenza non medica
                    </motion.p>

                    <h1 className="font-serif font-light text-ink text-[clamp(2.9rem,7.5vw,6.5rem)] leading-[0.98] tracking-[-0.02em]">
                        {HERO_LINES.map((line, i) => (
                            <span className="mask-line" key={i}>
                                <motion.span
                                    className="block"
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        duration: 1,
                                        delay: 0.2 + i * 0.14,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-mutedwarm"
                    >
                        Spese, visite mediche, accompagnamento e passeggiate:
                        supporto attivo quotidiano per tutte le età, qui in
                        Emilia-Romagna, con la calma e la cura di un amico.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="tel:+393522553391"
                            data-testid="hero-cta-call"
                            className="group inline-flex items-center gap-3 rounded-full bg-terracotta px-7 py-4 text-base font-bold text-cream transition-all duration-300 hover:bg-terracotta-dark hover:-translate-y-0.5 hover:shadow-xl hover:shadow-terracotta/25"
                        >
                            <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
                            352 255 3391
                        </a>
                        <a
                            href="mailto:serviziamicielena@gmail.com"
                            data-testid="hero-cta-email"
                            className="group inline-flex items-center gap-3 rounded-full border border-ink/20 px-7 py-4 text-base font-bold text-ink transition-all duration-300 hover:bg-sand hover:border-ink/10 hover:-translate-y-0.5"
                        >
                            <Mail className="h-5 w-5 text-terracotta transition-transform duration-300 group-hover:-rotate-12" />
                            Scrivici una mail
                        </a>
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-whatsapp"
                            className="group inline-flex items-center gap-3 rounded-full border border-ink/20 px-7 py-4 text-base font-bold text-ink transition-all duration-300 hover:bg-[#25D366]/10 hover:border-[#25D366]/40 hover:-translate-y-0.5"
                        >
                            <WhatsAppIcon className="h-5 w-5 text-[#25D366] transition-transform duration-300 group-hover:scale-110" />
                            WhatsApp
                        </a>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                        className="mt-10 flex flex-wrap gap-2.5"
                    >
                        {CHIPS.map((c) => (
                            <li
                                key={c}
                                className="rounded-full bg-stonewarm border border-line px-4 py-1.5 text-sm font-semibold text-mutedwarm"
                            >
                                {c}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <div className="lg:col-span-5 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="relative mx-auto max-w-[420px] lg:max-w-none"
                    >
                        <motion.div style={{ y: yImg }} className="arch-frame relative">
                            <img
                                src={HERO_IMG}
                                alt="Due amiche di età diverse tornano a casa con la spesa al tramonto"
                                className="h-[420px] sm:h-[520px] lg:h-[600px] w-full object-cover"
                                loading="eager"
                            />
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 bg-ink/5"
                            />
                        </motion.div>

                        <motion.div
                            style={{ y: yBadge }}
                            className="absolute -left-4 sm:-left-8 bottom-10 animate-float-slow"
                        >
                            <div className="flex items-center gap-3 rounded-2xl border border-line bg-cream/90 backdrop-blur px-5 py-4 shadow-xl shadow-ink/5">
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
                                    <Heart className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="text-sm font-bold text-ink">
                                        Non medico, ma su misura
                                    </p>
                                    <p className="text-xs font-semibold text-mutedwarm">
                                        Per tutte le età · 5 giorni su 7
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
